import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { FetchableState } from "..";
import { ApiRequest, serializeModel } from "../../../api";
import {
  AssetBody,
  AssetObject,
  Company,
  SiteItem,
} from "@vviinn/user-managment-client-ts";

type AssetsState = FetchableState & {
  assets: AssetObject[];
  errorMessage?: string;
};

type BaseRequest = {
  companyId: Company["id"];
  siteId: SiteItem["id"];
};

type UpsertAssetRequest = BaseRequest & {
  assetBody: AssetBody;
};

type DeleteAssetRequest = BaseRequest & {
  assetId: string;
};

const initialState: AssetsState = {
  assets: [],
  loading: false,
  error: false,
  status: "idle",
};
const assetsSlice = createSlice({
  name: "assets",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchAssets.pending, (state) => {
      state.loading = true;
      state.error = false;
      state.status = "loading";
      state.errorMessage = undefined;
    });
    builder.addCase(fetchAssets.fulfilled, (state, { payload }) => {
      state.assets = payload;
      state.error = false;
      state.loading = false;
      state.status = "fulfilled";
      state.errorMessage = undefined;
    });
    builder.addCase(fetchAssets.rejected, (state, { error }) => {
      state.loading = false;
      state.error = true;
      state.status = "error";
      state.errorMessage = error.message;
    });
  },
});

export default assetsSlice.reducer;

export const fetchAssets = createAsyncThunk(
  "assets/fetch",
  async ({ api, value }: ApiRequest<BaseRequest>) => {
    const { companyId, siteId } = value;
    const response = await api.getSiteAssets(companyId, siteId);
    return response.value.map(serializeModel);
  },
);

export const upsertAsset = createAsyncThunk(
  "assets/upsert",
  async ({ api, value }: ApiRequest<UpsertAssetRequest>) => {
    const { companyId, siteId, assetBody } = value;
    const response = await api.upsertSiteAsset(companyId, siteId, assetBody);
    return serializeModel(response.value);
  },
);

export const deleteAsset = createAsyncThunk(
  "assets/delete",
  async ({ api, value }: ApiRequest<DeleteAssetRequest>) => {
    const { companyId, siteId, assetId } = value;
    const response = await api.deleteSiteAsset(companyId, siteId, assetId);
    return serializeModel(response.value);
  },
);
